import { useEffect, useRef, useState } from "react";

const QUESTIONS = [
  "Tell me about yourself.",
  "Explain React simply.",
  "How does useState work?",
  "How do you handle API calls?",
  "Any questions for me?",
];

const INTERRUPTS = [
  "Wait. Be specific.",
  "You're going off track.",
  "Answer faster.",
  "Why?",
  "That sounds generic.",
];

const OPENAI_KEY = process.env.REACT_APP_OPENAI_API_KEY;

export default function PressureVoiceInterview() {
  const [state, setState] = useState("ringing"); // ringing | active | ended
  const [voices, setVoices] = useState([]);
  const [seconds, setSeconds] = useState(0);

  const [qIndex, setQIndex] = useState(0);
  const [liveText, setLiveText] = useState("");

  const [correct, setCorrect] = useState(0);
  const [weak, setWeak] = useState(0);
  const [wrong, setWrong] = useState(0);

  const recognitionRef = useRef(null);
  const speakingRef = useRef(false);
  const finalAnswerRef = useRef("");

  /* ---------------- TIMER ---------------- */
  useEffect(() => {
    if (state === "active") {
      const t = setInterval(() => setSeconds((s) => s + 1), 1000);
      return () => clearInterval(t);
    }
  }, [state]);

  /* ---------------- VOICES ---------------- */
  useEffect(() => {
    const load = () => setVoices(window.speechSynthesis.getVoices());
    load();
    window.speechSynthesis.onvoiceschanged = load;
  }, []);

  const voice =
    voices.find((v) => v.name.includes("Google")) ||
    voices.find((v) => v.name.includes("Microsoft")) ||
    voices[0];

  /* ---------------- SPEECH RECOGNITION ---------------- */
  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;

    const rec = new SR();
    rec.lang = "en-US";
    rec.interimResults = true;
    rec.continuous = false; // 🔥 IMPORTANT

    rec.onresult = (e) => {
      let text = "";
      for (let i = 0; i < e.results.length; i++) {
        text += e.results[i][0].transcript + " ";
      }
      finalAnswerRef.current = text.trim();
      setLiveText(text.trim());
    };

    rec.onend = () => {
      if (finalAnswerRef.current) {
        analyseAnswer(finalAnswerRef.current);
      }
    };

    recognitionRef.current = rec;
  }, []);

  /* ---------------- SPEAK ---------------- */
  const speak = (text, onEnd) => {
    speakingRef.current = true;
    window.speechSynthesis.cancel();

    const u = new SpeechSynthesisUtterance(text);
    u.voice = voice;
    u.rate = 0.95;

    u.onend = () => {
      speakingRef.current = false;
      onEnd && onEnd();
    };

    window.speechSynthesis.speak(u);
  };

  /* ---------------- LISTEN ---------------- */
  const listenOnce = () => {
    setLiveText("");
    finalAnswerRef.current = "";

    // random pressure interrupt
    if (Math.random() > 0.6) {
      setTimeout(() => {
        speak(INTERRUPTS[Math.floor(Math.random() * INTERRUPTS.length)]);
      }, 2000);
    }

    recognitionRef.current?.start();
  };

  /* ---------------- ANALYSE ---------------- */
  const analyseAnswer = async (answer) => {
    const res = await fetch("https://api.openai.com/v1/chat/completions", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${OPENAI_KEY}`,
      },
      body: JSON.stringify({
        model: "gpt-4o-mini",
        messages: [
          {
            role: "system",
            content:
              "You are a strict interviewer. Respond naturally. End with [Correct], [Weak], or [Wrong].",
          },
          {
            role: "user",
            content: `Question: ${QUESTIONS[qIndex]}\nAnswer: ${answer}`,
          },
        ],
      }),
    });

    const data = await res.json();
    const reply = data.choices[0].message.content;

    if (reply.includes("[Correct]")) setCorrect((c) => c + 1);
    else if (reply.includes("[Weak]")) setWeak((w) => w + 1);
    else setWrong((w) => w + 1);

    const clean = reply.replace(/\[(Correct|Weak|Wrong)\]/g, "").trim();

    speak(clean, () => {
      const next = qIndex + 1;
      if (next < QUESTIONS.length) {
        setQIndex(next);
        speak(QUESTIONS[next], listenOnce);
      } else {
        setState("ended");
      }
    });
  };

  /* ---------------- START ---------------- */
  const startCall = () => {
    setState("active");
    speak(QUESTIONS[0], listenOnce);
  };

  /* ---------------- UI ---------------- */

  if (state === "ringing") {
    return (
      <div style={styles.center}>
        <h2>📞 Incoming Interview Call</h2>
        <button style={styles.accept} onClick={startCall}>
          Accept
        </button>
      </div>
    );
  }

  if (state === "ended") {
    return (
      <div style={styles.center}>
        <h2>🔥 Pressure Interview Result</h2>
        <p>✅ Correct: {correct}</p>
        <p>⚠️ Weak: {weak}</p>
        <p>❌ Wrong: {wrong}</p>
        <h3>
          Score: {correct}/{QUESTIONS.length}
        </h3>
      </div>
    );
  }

  return (
    <div style={styles.center}>
      <h3>🎧 Pressure Interview</h3>
      <p>
        {Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, "0")}
      </p>
      <p>🟢 Mic Live • Pressure Mode</p>
      <p style={{ opacity: 0.8 }}>📝 {liveText || "Listening..."}</p>
    </div>
  );
}

/* ---------------- STYLES ---------------- */

const styles = {
  center: {
    height: "100vh",
    background: "#020617",
    color: "#fff",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    padding: 30,
  },
  accept: {
    marginTop: 20,
    padding: "14px 30px",
    borderRadius: 30,
    border: "none",
    background: "#ef4444",
    color: "#fff",
    fontSize: 16,
    cursor: "pointer",
  },
};
